/**
 * <s-product-gallery> — PDP gallery behaviour layered on server-rendered markup.
 *
 * - Desktop: slides marked [data-gallery-extra] start collapsed (md:hidden). The
 *   [data-gallery-expand] button reveals them in place and moves focus to the first one.
 * - Mobile: the [data-product-gallery] scroller swipes natively; [data-gallery-dot] buttons
 *   and the [data-gallery-current] counter follow the visible slide.
 * - Variants: when the product-form partial re-renders, [data-variant-media-id] names the
 *   selected variant's image; that one slide is revealed (if collapsed) and brought into view.
 */
const DESKTOP = window.matchMedia('(min-width: 768px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #formObserver;
  #lastVariantMediaId = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.scroller = this.querySelector('[data-product-gallery]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.counter = this.querySelector('[data-gallery-current]');
    this.expandButton = this.querySelector('[data-gallery-expand]');

    this.expandButton?.addEventListener('click', () => this.expand(true), opts);

    for (const dot of this.dots) {
      dot.addEventListener('click', () => this.#scrollToIndex(Number(dot.dataset.galleryDot)), opts);
    }

    if (this.scroller && this.slides.length > 1 && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, { root: this.scroller, threshold: 0.6 });
      this.slides.forEach((slide) => this.#observer.observe(slide));
    }

    // The product-form partial is replaced on variant change; watch it for the media marker.
    const form = document.querySelector('[data-product-form]')?.parentElement || document.querySelector('s-product-form');
    if (form) {
      this.#formObserver = new MutationObserver(() => this.#syncVariantMedia(false));
      this.#formObserver.observe(form, { childList: true, subtree: true });
    }
    this.#syncVariantMedia(true);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#formObserver?.disconnect();
  }

  expand(moveFocus = false) {
    const extras = this.slides.filter((slide) => slide.hasAttribute('data-gallery-extra'));
    if (!extras.length || this.hasAttribute('data-expanded')) return;
    extras.forEach((slide) => slide.classList.remove('md:hidden'));
    this.setAttribute('data-expanded', '');
    if (this.expandButton) {
      this.expandButton.setAttribute('aria-expanded', 'true');
      this.expandButton.hidden = true;
      this.expandButton.classList.remove('md:flex');
    }
    if (moveFocus) {
      const focusable = extras[0].querySelector('button, a, video, iframe, [tabindex]');
      focusable?.focus({ preventScroll: false });
    }
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.slides.indexOf(entry.target);
      if (index < 0) continue;
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(index + 1);
    }
  };

  #scrollToIndex(index) {
    const slide = this.slides[index];
    if (!slide || !this.scroller) return;
    this.scroller.scrollTo({
      left: slide.offsetLeft - this.scroller.offsetLeft,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
  }

  #syncVariantMedia(initial) {
    const marker = document.querySelector('[data-variant-media-id]');
    const mediaId = marker?.dataset.variantMediaId;
    if (!mediaId || mediaId === this.#lastVariantMediaId) return;
    const isFirstRead = this.#lastVariantMediaId === null;
    this.#lastVariantMediaId = mediaId;

    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index < 0) return;
    const slide = this.slides[index];

    // Reveal only the selected variant's photo when it sits past the first four; "View all photos" still expands the rest.
    if (slide.hasAttribute('data-gallery-extra') && !this.hasAttribute('data-expanded')) {
      slide.classList.remove('md:hidden');
    }


    // On first load only reveal; don't move the page. After a variant change, bring the image into view.
    if (initial || isFirstRead) {
      if (!DESKTOP.matches && index > 0) {
        this.scroller.scrollLeft = slide.offsetLeft - this.scroller.offsetLeft;
      }
      return;
    }

    if (DESKTOP.matches) {
      const rect = slide.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.top < window.innerHeight * 0.6;
      if (!inView) slide.scrollIntoView({ block: 'start', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    } else {
      this.#scrollToIndex(index);
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
